import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  Alert, Typography, Tooltip, useTheme, useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import StockBalanceAutocomplete from '../../components/StockBalanceAutocomplete';
import BatchChip from '../../components/BatchChip';
import {
  getStores,
  createTransfer,
  newTransferReferenceId,
  buildTransferBody,
  normalizeTransferResponse,
} from '../../api/stockLedger';
import useAssignedStoreOptions from '../../hooks/useAssignedStoreOptions';
import { storeDisplayName } from '../../utils/storeEntity';
import { formatCurrency, formatDate, formatQty, sanitizeQtyInput } from '../../utils';
import {
  TRANSFER_REASONS,
  TRANSFER_REASON_OTHER,
  resolveTransferReason,
} from '../../constants/stockTransfer';
import { getStoreActorId } from '../../hooks/useStoreScope';
import useItemNames from '../../hooks/useItemNames';
import { IPD_COLORS } from '../../theme';

const ACTION_BAR_SX = {
  display: 'flex',
  gap: 1.5,
  flexWrap: 'wrap',
  width: { xs: '100%', sm: 'auto' },
  '& > button': { flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 'auto' } },
};

function normalizeStoreList(d) {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Batches as the catalogue ordered them — earliest expiry first. */
function batchesOf(item) {
  return [...(item?.batches || [])].sort(
    (a, b) => (Number(a.fefo_rank) || 0) - (Number(b.fefo_rank) || 0),
  );
}

function fefoBatchOf(item) {
  return batchesOf(item)[0] || null;
}

/** Sale rate per unit of the batch this line will actually draw from. */
function batchSaleRate(batch) {
  const raw = batch?.sale_price ?? batch?.mrp;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function availableOf(item) {
  const n = Number(item?.total_available_quantity);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Focus the real control inside a field wrapper. A select renders its focusable
 * element as a div, so targeting `input` alone would land on the hidden one.
 */
function focusField(node) {
  if (!node) return;
  const target = node.querySelector('.MuiSelect-select')
    || node.querySelector('input:not([type="hidden"])');
  target?.focus();
}

/** Focus after the current render commits, or the select that just closed steals it back. */
function focusSoon(getNode) {
  window.requestAnimationFrame(() => {
    const node = getNode();
    if (node instanceof HTMLElement && node.tagName === 'INPUT') node.focus();
    else focusField(node);
  });
}

export default function TransferForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const listPath = url.replace('/transfer/new', '/transfers');

  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [error, setError] = useState(null);
  const rowSeq = useRef(0);
  const [rows, setRows] = useState(() => [{ key: 'row-0', itemId: '', item: null, quantity: '', expanded: false }]);

  const toStoreRef = useRef(null);
  const rowRefs = useRef(new Map());

  const { storeLocked, activeStoreId, isStoreScoped, storeOptions } = useAssignedStoreOptions(stores);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      from_store_id: '',
      to_store_id: '',
      transfer_reason: TRANSFER_REASONS[0].value,
      custom_reason: '',
      expected_date: '',
    },
  });

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores()
      .then(d => setStores(normalizeStoreList(d)))
      .catch(() => setStores([]));
  }, []);

  const fromStoreVal = watch('from_store_id');
  const toStoreVal = watch('to_store_id');
  const reasonVal = watch('transfer_reason');
  const expectedDateVal = watch('expected_date');

  // Stock leaves an assigned store; lock only when the user has exactly one.
  const lockFromStore = storeLocked && stores.some(s => (s.store_id || s.id) === activeStoreId);

  useEffect(() => {
    if (lockFromStore) setValue('from_store_id', activeStoreId);
    else if (isStoreScoped && activeStoreId && !fromStoreVal) setValue('from_store_id', activeStoreId);
  }, [lockFromStore, activeStoreId, isStoreScoped, fromStoreVal, setValue]);

  // Items come from the source store's catalogue, so a source change invalidates
  // every line that was picked against the previous store.
  const prevFromStore = useRef(fromStoreVal);
  useEffect(() => {
    if (prevFromStore.current === fromStoreVal) return;
    prevFromStore.current = fromStoreVal;
    rowSeq.current += 1;
    rowRefs.current.clear();
    setRows([{ key: `row-${rowSeq.current}`, itemId: '', item: null, quantity: '', expanded: false }]);
  }, [fromStoreVal]);

  // Exactly one blank row always trails the list, so a new line never costs a click.
  useEffect(() => {
    setRows((prev) => {
      const last = prev[prev.length - 1];
      if (last && !last.itemId) return prev;
      rowSeq.current += 1;
      return [...prev, { key: `row-${rowSeq.current}`, itemId: '', item: null, quantity: '', expanded: false }];
    });
  }, [rows]);

  function registerRef(key, field) {
    return (node) => {
      const entry = rowRefs.current.get(key) || {};
      entry[field] = node;
      rowRefs.current.set(key, entry);
    };
  }

  const updateRow = useCallback((key, patch) => {
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...patch } : row)));
  }, []);

  const removeRow = useCallback((key) => {
    rowRefs.current.delete(key);
    setRows((prev) => {
      const next = prev.filter(row => row.key !== key);
      if (next.length) return next;
      rowSeq.current += 1;
      return [{ key: `row-${rowSeq.current}`, itemId: '', item: null, quantity: '', expanded: false }];
    });
  }, []);

  const duplicateItemIds = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    rows.forEach((row) => {
      if (!row.itemId) return;
      if (seen.has(row.itemId)) dupes.add(row.itemId);
      seen.add(row.itemId);
    });
    return dupes;
  }, [rows]);

  /** Live per-row message — shown as the user types, not on submit. */
  const rowErrors = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (!row.itemId) return;
      if (duplicateItemIds.has(row.itemId)) {
        map.set(row.key, { field: 'item', message: 'This item is already on the transfer.' });
        return;
      }
      if (!row.item?.base_uom_id) {
        map.set(row.key, { field: 'item', message: 'No base UOM configured for this item.' });
        return;
      }
      if (row.quantity === '') {
        map.set(row.key, { field: 'quantity', message: 'Enter a quantity.' });
        return;
      }
      const qty = Number(row.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        map.set(row.key, { field: 'quantity', message: 'Quantity must be greater than 0.' });
        return;
      }
      const available = availableOf(row.item);
      if (qty > available) {
        map.set(row.key, { field: 'quantity', message: `Only ${formatQty(available)} available.` });
      }
    });
    return map;
  }, [rows, duplicateItemIds]);

  const filledRows = useMemo(() => rows.filter(row => row.itemId), [rows]);
  const expectedDateError = expectedDateVal && expectedDateVal < todayIso()
    ? 'Expected date cannot be in the past.'
    : null;
  const sameStore = Boolean(fromStoreVal) && fromStoreVal === toStoreVal;
  const reasonText = resolveTransferReason(reasonVal, watch('custom_reason'));
  const canSubmit = Boolean(fromStoreVal)
    && Boolean(toStoreVal)
    && !sameStore
    && filledRows.length > 0
    && rowErrors.size === 0
    && !expectedDateError
    && Boolean(reasonText)
    && !saving;

  function handleItemPicked(key, itemId, item) {
    updateRow(key, { itemId, item, quantity: itemId ? '1' : '', expanded: false });
    if (itemId) focusSoon(() => rowRefs.current.get(key)?.qty);
  }

  function handleQtyKeyDown(event, key) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (rowErrors.has(key)) return;
    // The trailing blank row already exists, so Enter just moves into it.
    const index = rows.findIndex(row => row.key === key);
    const next = rows[index + 1];
    if (next) focusSoon(() => rowRefs.current.get(next.key)?.item);
  }

  function handleRowKeyDown(event, key) {
    if (event.key === 'Escape') {
      event.preventDefault();
      updateRow(key, { itemId: '', item: null, quantity: '', expanded: false });
    }
  }

  async function onSubmit(data) {
    if (saving) return;
    setError(null);

    const items = filledRows.map(row => ({
      item_id: row.itemId,
      uom_id: row.item.base_uom_id,
      quantity: Number(row.quantity),
    }));

    setSaving(true);
    try {
      // API: POST /inventory/stock/transfers/ — every line in one request, so the
      // backend writes one transaction and a failure rolls all of them back.
      const res = await createTransfer(buildTransferBody({
        from_store_id: data.from_store_id,
        to_store_id: data.to_store_id,
        items,
        reference_id: newTransferReferenceId(),
        remarks: reasonText,
        expected_date: data.expected_date || undefined,
        transfer_reason: reasonText,
      }));
      const created = normalizeTransferResponse(res);
      toast.success(
        `Transfer ${created.transaction_number || ''} created with ${items.length} item(s).`.trim(),
      );
      history.push(
        created.transaction_id ? `${listPath}?highlight=${created.transaction_id}` : listPath,
      );
    } catch (e) {
      console.error('[TransferForm] transfer failed:', e);
      setError(e.message || 'Transfer failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const submit = handleSubmit(
    (formData) => { void onSubmit(formData); },
    (formErrors) => {
      const first = formErrors.from_store_id || formErrors.to_store_id || formErrors.transfer_reason;
      setError(first?.message || 'Please complete the transfer details.');
    },
  );

  function handleFormKeyDown(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && canSubmit) {
      event.preventDefault();
      submit();
    }
  }
  const allStoreOptions = stores.map(s => ({
    value: s.store_id || s.id,
    label: `${s.store_name} (${s.store_code})`,
  }));


  const actionButtons = (
    <>
      <InvButton
        variant="outlined"
        tone="teal"
        type="button"
        disabled={saving}
        onClick={() => history.push(listPath)}
      >
        Cancel
      </InvButton>
      <Tooltip title={canSubmit ? 'Ctrl + Enter' : 'Select both stores and add at least one valid item'}>
        <span>
          <InvButton type="button" onClick={submit} loading={saving} disabled={!canSubmit}>
            Create Transfer
          </InvButton>
        </span>
      </Tooltip>
    </>
  );

  return (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      onKeyDown={handleFormKeyDown}
      sx={{ pb: isMobile ? 10 : 0 }}
    >
      <PageHeader
        eyebrow="Inventory Management › Stock Transfers"
        title="Create Transfer"
        description="Select the source and destination store, then add items. Enter moves to the next field; Ctrl + Enter creates the transfer."
        actions={<Box sx={ACTION_BAR_SX}>{actionButtons}</Box>}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => !saving && setError(null)}>
          {error}
        </Alert>
      )}

      <SectionCard title="Transfer Details" sx={{ mb: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name="from_store_id"
              control={control}
              rules={{ required: 'From Store is required' }}
              render={({ field }) => (
                <InvSelect
                  label="From Store *"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value) focusSoon(() => toStoreRef.current);
                  }}
                  options={storeOptions}
                  error={errors.from_store_id}
                  disabled={saving || lockFromStore}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box ref={toStoreRef}>
              <Controller
                name="to_store_id"
                control={control}
                rules={{ required: 'To Store is required' }}
                render={({ field }) => (
                  <InvSelect
                    label="To Store *"
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      const first = rows[0];
                      if (e.target.value && first) {
                        focusSoon(() => rowRefs.current.get(first.key)?.item);
                      }
                    }}
                    options={allStoreOptions.filter(s => s.value !== fromStoreVal)}
                    error={errors.to_store_id}
                    helperText={sameStore ? 'From Store and To Store must be different.' : undefined}
                    disabled={saving || !fromStoreVal}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name="transfer_reason"
              control={control}
              rules={{ required: 'Transfer reason is required' }}
              render={({ field }) => (
                <InvSelect
                  label="Transfer Reason *"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  options={TRANSFER_REASONS}
                  error={errors.transfer_reason}
                  disabled={saving}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name="expected_date"
              control={control}
              render={({ field }) => (
                <InvTextField
                  {...field}
                  label="Expected Date"
                  type="date"
                  inputProps={{ min: todayIso() }}
                  error={expectedDateError}
                  helperText={expectedDateError}
                  InputLabelProps={{ shrink: true }}
                  disabled={saving}
                />
              )}
            />
          </Grid>
          {reasonVal === TRANSFER_REASON_OTHER && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="custom_reason"
                control={control}
                rules={{ required: 'Enter the reason for this transfer' }}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Reason *"
                    placeholder="Describe the reason"
                    error={errors.custom_reason}
                    helperText={errors.custom_reason?.message}
                    disabled={saving}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title={`Items to Transfer (${filledRows.length})`}>
        {!fromStoreVal && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select <strong>From Store</strong> first — only items stocked in that store can be transferred.
          </Alert>
        )}
        <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
          <Table
            size="small"
            sx={{
              minWidth: 900,
              '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 260 }}>Item</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 150 }}>Batch(Expiry)</TableCell>
                {/* <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Expiry</TableCell> */}
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 80 }}>UOM</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Available</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Sale Rate/unit</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Quantity</TableCell>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const rowError = rowErrors.get(row.key);
                const batches = batchesOf(row.item);
                const fefo = fefoBatchOf(row.item);
                const saleRate = batchSaleRate(fefo);
                return (
                  <React.Fragment key={row.key}>
                    <TableRow>
                      <TableCell sx={{ verticalAlign: 'top', pt: 1.5 }}>
                        <StockBalanceAutocomplete
                          storeId={fromStoreVal}
                          value={row.itemId}
                          inputRef={registerRef(row.key, 'item')}
                          onKeyDown={(e) => handleRowKeyDown(e, row.key)}
                          disabled={saving}
                          onChange={(itemId, item) => handleItemPicked(row.key, itemId, item)}
                        />
                        {rowError?.field === 'item' && (
                          <Typography sx={{ mt: 0.5, fontSize: 11, color: IPD_COLORS.error.text }}>
                            {rowError.message}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {fefo
                          ? <BatchChip batchNumber={fefo.batch_number} expiryDate={fefo.expiry_date} />
                          : <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>—</Typography>}
                      </TableCell>
                      {/* <TableCell sx={{ verticalAlign: 'middle', fontSize: 12 }}>
                        {fefo?.expiry_date ? formatDate(fefo.expiry_date) : '—'}
                      </TableCell> */}
                      <TableCell sx={{ verticalAlign: 'middle', fontSize: 12 }}>
                        {row.item?.uom_name || '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'middle', fontSize: 12 }}>
                        {row.item ? formatQty(availableOf(row.item)) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'middle', fontSize: 12 }}>
                        {saleRate != null ? formatCurrency(saleRate) : '—'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', pt: 1.5 }}>
                        <InvTextField
                          value={row.quantity}
                          inputRef={registerRef(row.key, 'qty')}
                          onChange={e => updateRow(row.key, { quantity: sanitizeQtyInput(e.target.value) })}
                          onKeyDown={(e) => handleQtyKeyDown(e, row.key)}
                          inputProps={{ inputMode: 'decimal', 'aria-label': 'Quantity' }}
                          error={rowError?.field === 'quantity' ? rowError.message : undefined}
                          helperText={rowError?.field === 'quantity' ? rowError.message : undefined}
                          disabled={saving || !row.itemId}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {batches.length > 1 && (
                          <Tooltip title={row.expanded ? 'Hide batches' : `Show all ${batches.length} batches`}>
                            <IconButton
                              size="small"
                              onClick={() => updateRow(row.key, { expanded: !row.expanded })}
                              sx={{ color: IPD_COLORS.primary }}
                            >
                              {row.expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          disabled={saving || !row.itemId}
                          onClick={() => removeRow(row.key)}
                          sx={{ color: IPD_COLORS.error.text }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {row.expanded && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ backgroundColor: IPD_COLORS.panelHeaderBg, py: 1 }}>
                          <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mb: 0.75 }}>
                            Batches are consumed earliest-expiry-first — the highlighted one goes out first.
                          </Typography>
                          <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Batch</TableCell>
                                <TableCell sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Expiry</TableCell>
                                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Available</TableCell>
                                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Sale Rate/unit</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {batches.map((batch, i) => {
                                const rate = batchSaleRate(batch);
                                return (
                                  <TableRow
                                    key={batch.stock_balance_id || `${batch.batch_number}-${i}`}
                                    sx={i === 0 ? { backgroundColor: IPD_COLORS.cardBg } : undefined}
                                  >
                                    <TableCell sx={{ fontSize: 12, fontWeight: i === 0 ? 700 : 400 }}>
                                      {batch.batch_number || '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>
                                      {batch.expiry_date ? formatDate(batch.expiry_date) : '—'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: 12 }}>
                                      {formatQty(batch.available_quantity)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: 12 }}>
                                      {rate != null ? formatCurrency(rate) : '—'}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          Only items stocked in the From Store are listed, and expired batches are excluded.
          Press <strong>Enter</strong> on a quantity to start the next line.
        </Typography>
      </SectionCard>

      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            p: 1.5,
            backgroundColor: IPD_COLORS.cardBg,
            borderTop: `1px solid ${IPD_COLORS.border}`,
            boxShadow: '0 -4px 16px rgba(13,35,63,0.08)',
          }}
        >
          <Box sx={ACTION_BAR_SX}>{actionButtons}</Box>
        </Box>
      )}
    </Box>
  );
}
